import { EmptyStateAlt } from '@ovComponents/2-component/emptyState/emptyState';
import { Box, Grid, Skeleton, Typography } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type {
  HouseholdModelPortfolioAction,
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioWidgetOptions,
} from '../householdModelPortfolio.types';
import {
  buildHouseholdModelPortfolioModelDetailFields,
  isHouseholdModelPortfolioDetailsMissing,
} from '../householdModelPortfolio.utils';
import { HouseholdModelPortfolioDetailField } from './householdModelPortfolioDetailField';

type HouseholdModelPortfolioModelDetailsProps = {
  action: HouseholdModelPortfolioAction;
  options: HouseholdModelPortfolioWidgetOptions;
  model: HouseholdModelPortfolioModel | null;
  modelDetailsLoading: boolean;
  modelDetailsError?: Error;
};

const MODEL_DETAILS_SKELETON_KEYS = [
  'strategy',
  'name',
  'family',
  'risk-profile',
  'forecast-return',
  'forecast-deviation',
] as const;

const HouseholdModelPortfolioModelDetailsSkeleton = () => (
  <Grid
    container
    spacing={2}
    data-testid='household-model-portfolio-model-details-loading'
  >
    {MODEL_DETAILS_SKELETON_KEYS.map((skeletonKey) => (
      <Grid key={skeletonKey} size={{ xs: 12, sm: 6, md: 4 }}>
        <Skeleton
          variant='rectangular'
          width='40%'
          height={16}
          sx={{ mb: 1 }}
        />
        <Skeleton variant='rectangular' width='70%' height={20} />
      </Grid>
    ))}
  </Grid>
);

export const HouseholdModelPortfolioModelDetails = ({
  action,
  options,
  model,
  modelDetailsLoading,
  modelDetailsError,
}: HouseholdModelPortfolioModelDetailsProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['components', 'modelPortfolios', 'shared']);

  const fields = useMemo(
    () =>
      buildHouseholdModelPortfolioModelDetailFields(
        translateBackendString,
        model,
        options,
        t,
      ),
    [model, options, t],
  );

  const hasAttachedModel = action === 'edit';
  const detailsMissing = isHouseholdModelPortfolioDetailsMissing({
    action,
    model,
    modelDetailsLoading,
    modelDetailsError,
  });

  return (
    <Box data-testid='household-model-portfolio-model-details'>
      <Typography variant='titleMedium' sx={{ mb: 2 }}>
        {t('components:householdModelPortfolio.modelDetails.title')}
      </Typography>

      {modelDetailsLoading ? (
        <HouseholdModelPortfolioModelDetailsSkeleton />
      ) : modelDetailsError || detailsMissing ? (
        <Box data-testid='household-model-portfolio-model-details-error'>
          <EmptyStateAlt
            sx={{ height: '112px' }}
            title={t('components:householdModelPortfolio.modelDetails.error')}
            subtitle={t('shared:errorStateSubText')}
          />
        </Box>
      ) : !hasAttachedModel ? (
        <Box data-testid='household-model-portfolio-no-model-attached'>
          <EmptyStateAlt
            sx={{ height: '112px' }}
            title={t(
              'components:householdModelPortfolio.modelDetails.noModelAttached',
            )}
          />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {fields.map((field) => (
            <Grid key={field.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <HouseholdModelPortfolioDetailField
                testId={`household-model-portfolio-detail-field-${field.id}`}
                label={field.label}
                value={field.value}
                href={field.href}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
